/* eslint-disable camelcase */
import api from './axios'

export interface PropertyAgent {
  id: number
  first_name: string
  last_name: string
  agency_name: string
  phone: string
  whatsapp: string
  avatar_url: string | null
}

export interface Property {
  id: number
  title: string
  purpose: string
  property_type: string
  price: number
  price_period: string
  district: string
  sector: string
  bedrooms: string
  bathrooms: string
  size: number
  images: string[]
  status: string
  views: number
  agent: PropertyAgent | null
}

export interface PropertyDetail extends Property {
  address: string
  description: string
  amenities: string[]
}

export interface PropertyListResponse {
  data: Property[]
  total: number
  page: number
  per_page: number
  total_pages: number
}

export interface PropertyFilters {
  purpose?: string
  property_type?: string
  district?: string
  min_price?: number
  max_price?: number
  bedrooms?: string
  search?: string
  sort?: 'newest' | 'price_asc' | 'price_desc' | 'popular'
  page?: number
  limit?: number
}

export interface PublicAgent {
  id: number
  first_name: string
  last_name: string
  agency_name: string
  phone: string
  whatsapp: string
  bio: string
  experience: string
  specialisations: string[]
  avatar_url: string | null
  listings_count: number
}

export interface PublicAgentDetail extends PublicAgent {
  email: string
  license_number: string
  languages: string[]
  location: string
  joined_at: string
  active_listings_count: number
  sold_listings_count: number
  rating: number | null
  reviews_count: number
}

export const propertiesApi = {
  // ── Public ──────────────────────────────────────────────────────────────
  list (filters: PropertyFilters = {}) {
    return api.get<PropertyListResponse>('/properties', { params: filters })
  },

  get (id: number) {
    return api.get<PropertyDetail>(`/properties/${id}`)
  },

  // Public agents directory — GET /api/v1/agents
  listAgents (params: { limit?: number; page?: number } = {}) {
    return api.get<{ data: PublicAgent[]; total: number }>('/agents', { params })
  },

  // Public agent detail — GET /api/v1/agents/:id
  getAgent (id: number) {
    return api.get<PublicAgentDetail>(`/agents/${id}`)
  },

  // Agent's public property listings — GET /api/v1/agents/:id/properties
  getAgentProperties (id: number, params: PropertyFilters = {}) {
    return api.get<PropertyListResponse>(`/agents/${id}/properties`, { params })
  },

  // ── Agent ────────────────────────────────────────────────────────────────
  agentList (params: { status?: string; search?: string; page?: number; limit?: number } = {}) {
    return api.get<PropertyListResponse>('/agent/properties', { params })
  },

  agentGet (id: number) {
    return api.get<PropertyDetail>(`/agent/properties/${id}`)
  },

  agentCreate (formData: FormData) {
    return api.post<PropertyDetail>('/agent/properties', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  agentUpdate (id: number, formData: FormData) {
    // Use POST with _method override for multipart updates
    formData.append('_method', 'PUT')
    return api.post<PropertyDetail>(`/agent/properties/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  agentDelete (id: number) {
    return api.delete<{ message: string }>(`/agent/properties/${id}`)
  },

  // ── Admin ────────────────────────────────────────────────────────────────
  adminCreate (formData: FormData) {
    return api.post<PropertyDetail>('/admin/properties', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  adminUpdate (id: number, formData: FormData) {
    // Use POST with _method override for multipart updates
    formData.append('_method', 'PUT')
    return api.post<PropertyDetail>(`/admin/properties/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  adminList (params: {
    status?: string
    agent_id?: number
    property_type?: string
    purpose?: string
    search?: string
    page?: number
    limit?: number
  } = {}) {
    return api.get<PropertyListResponse>('/admin/properties', { params })
  },

  adminUpdateStatus (id: number, status: 'active' | 'rejected' | 'pending') {
    return api.put<PropertyDetail>(`/admin/properties/${id}/status`, { status })
  },

  adminDelete (id: number) {
    return api.delete<{ message: string }>(`/admin/properties/${id}`)
  }
}
